import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useChipMorphInternals, type ChipSnapshot } from './ChipSlotContext';
import styles from './ChipMorphLayer.module.css';

/**
 * The capsule, while it is between owners.
 *
 * The pill and the island are parented in different places and neither can be
 * moved (see `lib/chip-morph.ts`), so the morph cannot be a CSS transition on
 * either of them. Instead this layer takes over for the length of the swap: it
 * draws ONE fixed-position capsule, animates its rect from where the outgoing
 * chip sat to where the incoming one will sit, and cross-fades the two chips'
 * contents inside it. Both real chips are hidden for exactly that window, so
 * there is never a frame with two capsules and never one with none.
 *
 * Two details are what make it read as one object rather than a dissolve:
 *
 *  - **The SURFACE belongs to the container, not to the faces.** The box carries
 *    the fill and border, animated from the pill's soft green to the island's
 *    inverted skin, and each cloned face is stripped of its own. Cross-fading two
 *    opaque capsules instead turns muddy in the middle — you can see both, and
 *    neither is the thing you started with.
 *  - **The faces are clones of the real DOM** (`cloneNode`), not
 *    re-implementations. A second copy of either chip's markup would drift the
 *    moment someone edited the original, and the whole claim here is that this IS
 *    the same chip. Cloning the node rather than round-tripping `innerHTML` keeps
 *    it a copy of the element instead of a re-parse of its serialization — and
 *    keeps a chip built from user-influenced content (agent names, call state)
 *    away from an HTML parser it never needed to go through.
 */

const DURATION_MS = 420;
// Barely-there overshoot: the width change is the part the eye reads as "the
// same thing got wider", so it has to settle rather than stop dead.
const EASING = 'cubic-bezier(0.36, 1.06, 0.5, 1)';
// A chip appearing from nothing (or leaving to nothing) has no partner rect, so
// it scales about its own centre instead of travelling.
const POP_SCALE = 0.72;

type Plan = { from: ChipSnapshot | null; to: ChipSnapshot | null };

function place(snapshot: ChipSnapshot) {
  const { rect } = snapshot;
  return {
    transform: `translate(${rect.left}px, ${rect.top}px)`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
  };
}

function popped(snapshot: ChipSnapshot) {
  const { rect } = snapshot;
  const dx = (rect.width * (1 - POP_SCALE)) / 2;
  const dy = (rect.height * (1 - POP_SCALE)) / 2;
  return {
    transform: `translate(${rect.left + dx}px, ${rect.top + dy}px) scale(${POP_SCALE})`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
  };
}

function skin(snapshot: ChipSnapshot) {
  return {
    backgroundColor: snapshot.skin.backgroundColor,
    borderColor: snapshot.skin.borderColor,
    borderWidth: snapshot.skin.borderWidth,
  };
}

/**
 * The cloned chip is an inline capsule with its own fill; stretch it to whatever
 * the box is now and hand the fill to the box, so one surface morphs instead of
 * two dissolving.
 */
/**
 * Put a snapshot's cloned chip inside a face.
 *
 * Re-clones on every application: appending a node MOVES it, so handing the same snapshot to a face
 * twice (React re-runs this effect whenever the plan changes) would empty the first one. The
 * snapshot stays a pristine master copy and every face gets its own.
 */
function mountFace(host: HTMLElement | null, snapshot: ChipSnapshot | null) {
  if (!host) return;
  const clone = snapshot ? (snapshot.content.cloneNode(true) as HTMLElement) : null;
  host.replaceChildren(...(clone ? Array.from(clone.childNodes) : []));
}

function prepareFace(host: HTMLElement | null) {
  const clone = host?.firstElementChild as HTMLElement | undefined;
  if (!clone) return;
  clone.style.width = '100%';
  clone.style.height = '100%';
  clone.style.boxSizing = 'border-box';
  clone.style.justifyContent = 'center';
  clone.style.background = 'transparent';
  clone.style.borderColor = 'transparent';
}

export function ChipMorphLayer() {
  const api = useChipMorphInternals();
  const [mounted, setMounted] = useState(false);
  const [plan, setPlan] = useState<Plan | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const fromRef = useRef<HTMLDivElement>(null);
  const toRef = useRef<HTMLDivElement>(null);

  useEffect(() => setMounted(true), []);

  const morph = api?.state.morph ?? null;

  useLayoutEffect(() => {
    if (!api || !morph) {
      setPlan(null);
      return;
    }
    // Outgoing comes from the snapshot cache — by now React has already
    // unmounted it. Incoming is measured live: it is mounted and laid out,
    // merely unpainted.
    const from = morph.from ? api.snapshots.current[morph.from] ?? null : null;
    const to = morph.to ? api.readAnchor(morph.to) : null;
    if (!from && !to) {
      api.settle();
      return;
    }
    setPlan({ from, to });
  }, [api, morph]);

  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!api || !plan || !box) return;
    // Content first: `prepareFace` styles the cloned capsule, and the animation below measures
    // against a face that is already filled.
    mountFace(fromRef.current, plan.from);
    mountFace(toRef.current, plan.to);
    prepareFace(fromRef.current);
    prepareFace(toRef.current);
    // No Web Animations API (very old browser, or a test env) — settle rather
    // than leave the overlay parked over a hidden chip.
    if (typeof box.animate !== 'function') {
      api.settle();
      return;
    }
    const start = plan.from ?? plan.to!;
    const end = plan.to ?? plan.from!;
    const opts = { duration: DURATION_MS, easing: EASING, fill: 'both' as const };

    const boxAnim = box.animate(
      [
        { ...(plan.from ? place(start) : popped(start)), ...skin(start), opacity: plan.from ? 1 : 0 },
        { ...(plan.to ? place(end) : popped(end)), ...skin(end), opacity: plan.to ? 1 : 0 },
      ],
      opts,
    );
    // The content cross-fade is linear and generously overlapped, so the capsule
    // is never momentarily empty in the middle of its own travel.
    const fade = { duration: DURATION_MS, easing: 'linear', fill: 'both' as const };
    fromRef.current?.animate(
      [
        { opacity: 1, offset: 0 },
        { opacity: 1, offset: 0.08 },
        { opacity: 0, offset: 0.55 },
        { opacity: 0, offset: 1 },
      ],
      fade,
    );
    toRef.current?.animate(
      [
        { opacity: 0, offset: 0 },
        { opacity: 0, offset: 0.2 },
        { opacity: 1, offset: 0.8 },
        { opacity: 1, offset: 1 },
      ],
      fade,
    );

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      api.settle();
    };
    boxAnim.addEventListener('finish', finish);
    // Belt and braces: a backgrounded tab can park an animation indefinitely,
    // and a slot stuck mid-morph means no chip at all.
    const timer = window.setTimeout(finish, DURATION_MS + 200);
    return () => {
      boxAnim.removeEventListener('finish', finish);
      window.clearTimeout(timer);
    };
  }, [api, plan]);

  if (!mounted || !plan || typeof document === 'undefined') return null;

  return createPortal(
    <div className={styles.layer} aria-hidden data-testid="chip-morph-layer">
      <div ref={boxRef} className={styles.box}>
        {/* Empty on purpose — the cloned chip is put in by `mountFace` in the layout effect above,
            so the real element is copied rather than its markup re-parsed. */}
        {plan.from && <div ref={fromRef} className={styles.face} />}
        {plan.to && <div ref={toRef} className={styles.face} />}
      </div>
    </div>,
    document.body,
  );
}
